import React, { useState } from 'react';
import {
  Box,
  Button,
  useToast,
  Text,
  VStack,
  Icon,
  Switch,
  FormControl,
  FormLabel,
  HStack,
} from '@chakra-ui/react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { registerLocale } from "react-datepicker";
import es from "date-fns/locale/es";
import GrillaEffect from "views/Grillas/GrillaEffect";
import dayjs from "dayjs";
import 'dayjs/locale/es';
import { IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useHistory } from 'react-router-dom';

registerLocale("es", es);
const theme = createTheme({
  palette: {}
});

const RegistrosSinCoincidencias = () => {
  const defaultFecha = [
    { id: 'fecha_valor', value: [dayjs().subtract(60, 'days'), dayjs(new Date())] }
  ];
  const [registros, setRegistros] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  // Estado para el filtro de tipo de registro
  const [tipoRegistro, setTipoRegistro] = useState('todos'); // 'todos', 'lops', 'extbc'
  const toast = useToast();
  const history = useHistory();

  // Función para limpiar todos los filtros
  const handleClearFilters = () => {
    setFilters([]);
    toast({
      title: 'Filtros limpiados',
      description: 'Se han eliminado todos los filtros aplicados.',
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  // Función para cambiar el tipo de registro
  const handleTipoRegistroChange = (nuevoTipo) => {
    setTipoRegistro(nuevoTipo);
    toast({
      title: 'Filtro de tipo actualizado',
      description: `Mostrando: ${nuevoTipo === 'todos' ? 'Todos los registros' : nuevoTipo === 'lops' ? 'Solo LOPS' : 'Solo EXTBC'}`,
      status: 'info',
      duration: 2000,
      isClosable: true,
    });
  };

  // Componente para mostrar cuando no hay resultados
  const NoResultsMessage = () => (
    <VStack spacing={4} p={8} textAlign="center">
      <Icon as={SearchOffIcon} boxSize={12} color="gray.400" />
      <Text fontSize="lg" color="gray.600">
        No se encontraron registros sin coincidencias con los filtros aplicados
      </Text>
      <Text fontSize="sm" color="gray.500">
        Intenta ajustar los criterios de búsqueda o limpiar los filtros
      </Text>
      <Button
        leftIcon={<ClearAllIcon />}
        colorScheme="blue"
        variant="outline"
        onClick={handleClearFilters}
      >
        Limpiar todos los filtros
      </Button>
    </VStack>
  );

  // Custom toolbar
  const customTopToolbar = (
    <Box display="flex" alignItems="center" gap={2}>
      <IconButton
        color="secondary"
        disabled={filters.length === 0}
        onClick={handleClearFilters}
        title="Limpiar Filtros"
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <ClearAllIcon sx={{ mr: 1 }} />
        Limpiar Filtros
      </IconButton>
      
      {/* Filtros de tipo de registro */}
      <HStack spacing={2} ml={4}>
        <Icon as={FilterAltIcon} color="gray.600" />
        <FormControl display="flex" alignItems="center">
          <FormLabel htmlFor="tipo-registro" mb="0" fontSize="sm" mr={2}>
            Mostrar:
          </FormLabel>
          <select
            id="tipo-registro"
            value={tipoRegistro}
            onChange={(e) => handleTipoRegistroChange(e.target.value)}
            style={{
              padding: '4px 8px',
              borderRadius: '4px',
              border: '1px solid #ccc',
              fontSize: '14px'
            }}
          >
            <option value="todos">Todos los registros</option>
            <option value="lops">Solo LOPS</option>
            <option value="extbc">Solo EXTBC</option>
          </select>
        </FormControl>
      </HStack>

      <Button
        variant="contained"
        color="secondary"
        onClick={() => history.push('/admin/conciliarops')}
        leftIcon={<ArrowBackIcon />}
      >
        Volver a Conciliar OPS
      </Button>
      <span>
        {filters.length > 0 && ` | ${filters.length} filtros aplicados`}
        {` | Tipo: ${tipoRegistro === 'todos' ? 'Todos' : tipoRegistro.toUpperCase()}`}
      </span>
    </Box>
  );

  // Callback para selección de filas desde GrillaEffect
  const onRowSelectionChange = (rowSelection, table) => {
    const selected = Object.keys(rowSelection)
      .filter(rowId => rowSelection[rowId])
      .map(rowId => table.getRowModel().rows.find(r => r.id === rowId)?.original)
      .filter(Boolean);
    setSelectedRows(selected);
  };

  // Columnas para LOPS y EXTBC combinados
  const columnNames = {
    tipo: "Tipo",
    fecha_movimiento: "Fecha Movimiento",
    fecha_valor: "Fecha Valor", 
    num_comprobante: "N° Comprobante/Referencia",
    descripcion: "Descripción",
    razonSocial: "Razón Social",
    debito: "Débito",
    credito: "Crédito",
    descripcionConcepto: "Concepto/Cuenta",
    id_registro: "ID Registro",
  };

  const colsGrillaHidden = {
    id_registro: false,
    id_comprobante_item: false,
    id_comprobante: false,
  };

  return (
    <Box p={4} mt="22%">
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
        <ThemeProvider theme={theme}>
          <VStack spacing={4} align="stretch">
            <Box>
              <Text fontSize="2xl" fontWeight="bold" color="gray.700" mb={2}>
                Registros Sin Coincidencias
              </Text>
              <Text fontSize="md" color="gray.600">
                Registros que no aparecen en ninguna de las tablas de coincidencias de ConciliarOps
              </Text>
            </Box>

            {registros.length === 0 && !isLoading && filters.length > 0 ? (
              <NoResultsMessage />
            ) : (
              <GrillaEffect
                data={registros}
                updateData={setRegistros}
                filters={filters}
                updateFilters={setFilters}
                colsHeaders={columnNames}
                hiddenColumns={colsGrillaHidden}
                method='GET'
                baseURI={`${process.env.REACT_APP_API_URL}/tesoreriacomprobantes/items/registros-sin-coincidencias`}
                customTopToolbar={customTopToolbar}
                onRowSelectionChange={onRowSelectionChange}
                isLoading={isLoading}
                additionalParams={{ tipo_registro: tipoRegistro }}
                rowSelectionEnabled={true}
              />
            )}
          </VStack>
        </ThemeProvider>
      </LocalizationProvider>
    </Box>
  );
};

export default RegistrosSinCoincidencias;
